import React, { createContext, useContext, useState, useCallback, useEffect } from 'react'

const AuthContext = createContext(null)

function getStoredAuth() {
  try {
    const raw = localStorage.getItem('authUser')
    const token = raw ? JSON.parse(raw) : null
    const userData = JSON.parse(localStorage.getItem('userData') || 'null')
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]')
    return { token, userData, permissions }
  } catch {
    return { token: null, userData: null, permissions: [] }
  }
}

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState(getStoredAuth)

  const login = useCallback((token, userData, permissions) => {
    localStorage.setItem('authUser', JSON.stringify(token))
    localStorage.setItem('userData', JSON.stringify(userData))
    localStorage.setItem('permissions', JSON.stringify(permissions))
    setAuth({ token, userData, permissions })
  }, [])

  const logout = useCallback(() => {
    localStorage.clear()
    setAuth({ token: null, userData: null, permissions: [] })
    window.location.href = '/login'
  }, [])

  const updateUserData = useCallback((userData) => {
    localStorage.setItem('userData', JSON.stringify(userData))
    setAuth(prev => ({ ...prev, userData }))
  }, [])

  const updatePermissions = useCallback((permissions) => {
    localStorage.setItem('permissions', JSON.stringify(permissions))
    setAuth(prev => ({ ...prev, permissions }))
  }, [])

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'authUser' && !e.newValue) {
        setAuth({ token: null, userData: null, permissions: [] })
      }
    }
    window.addEventListener('storage', handleStorage)
    return () => window.removeEventListener('storage', handleStorage)
  }, [])

  const value = {
    token: auth.token?.key || null,
    tokenObj: auth.token,
    user: auth.userData,
    permissions: auth.permissions,
    isAuthenticated: !!auth.token?.key,
    login,
    logout,
    updateUserData,
    updatePermissions,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
